import React from "react";
import * as Sentry from "@sentry/react";
import { isChunkLoadError, isDevServer, recoverFromChunkError } from "../../utils/chunkRecovery";

// Shows the caught error in Vite's own error overlay, which lists the file and stack.
const showDevOverlay = (error: any) => {
  if (typeof document === "undefined" || typeof customElements === "undefined") {
    return;
  }
  const Overlay: any = customElements.get("vite-error-overlay");
  if (Overlay && !document.querySelector("vite-error-overlay")) {
    document.body.appendChild(new Overlay(error));
  }
};

interface IRootErrorBoundaryProps {
  // Render nothing on failure (for optional UI like banners) instead of the recovery screen.
  silent?: boolean;
  children?: React.ReactNode;
}

interface IRootErrorBoundaryState {
  error: any;
  // True while chunk recovery may still reload the page; the recovery screen waits for it.
  recovering: boolean;
}

// Lives in the main bundle, so it must not depend on styles or components that
// ship in a lazy chunk — inline styles only.
const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  padding: "24px",
  textAlign: "center",
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  color: "#1f2937",
  background: "#ffffff"
};

const buttonStyle: React.CSSProperties = {
  marginTop: "16px",
  padding: "10px 20px",
  border: "none",
  borderRadius: "8px",
  background: "#365de6",
  color: "#ffffff",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer"
};

export default class RootErrorBoundary extends React.Component<
  IRootErrorBoundaryProps,
  IRootErrorBoundaryState
> {
  public static getDerivedStateFromError(error: any) {
    return { error, recovering: isChunkLoadError(error) && !isDevServer };
  }

  public state: IRootErrorBoundaryState = { error: null, recovering: false };
  private mounted = false;

  public componentDidMount() {
    this.mounted = true;
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  public componentDidCatch(error: any, info: any) {
    // A banner chunk that fails to load is skipped on purpose, so it is not reported.
    if (!(this.props.silent && isChunkLoadError(error))) {
      Sentry.captureException(error, {
        extra: { componentStack: info && info.componentStack }
      });
    }
    if (isDevServer) {
      // Errors thrown while the entry module reloads the page are noise.
      if ((window as any).__groupAppReloading) {
        return;
      }
      console.error("RootErrorBoundary caught:", error);
      showDevOverlay(error);
      return;
    }
    if (isChunkLoadError(error) && !this.props.silent) {
      recoverFromChunkError((error && error.message) || "render failed").then((willReload) => {
        if (!willReload && this.mounted) {
          this.setState({ recovering: false });
        }
      });
    }
  }

  public render() {
    if (!this.state.error) {
      return this.props.children || null;
    }
    if (this.props.silent || this.state.recovering) {
      return null;
    }
    return (
      <div style={containerStyle} role="alert">
        <h1 style={{ fontSize: "20px", margin: "0 0 8px" }}>
          Something went wrong loading GroupApp
        </h1>
        <p style={{ margin: 0, color: "#6b7280" }}>
          The page could not finish loading. Reloading usually fixes this.
        </p>
        {isDevServer && (
          <pre style={{ marginTop: "16px", maxWidth: "800px", whiteSpace: "pre-wrap", textAlign: "left", fontSize: "12px", color: "#b91c1c" }}>
            {String((this.state.error && this.state.error.stack) || this.state.error)}
          </pre>
        )}
        <button type="button" style={buttonStyle} onClick={this.reload}>
          Reload
        </button>
      </div>
    );
  }

  private reload = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };
}
